import type { AiProvider, AiSettingsUpdate, RedactionRule } from '@/stores/staff/aiSettings'

// The provider drivers and the redactor rules of the contract (AiProvider and
// RedactionRule in the OpenAPI spec). `satisfies` refuses a value that the
// contract does not have.
export const PROVIDERS = [
  'anthropic',
  'openai',
  'gemini',
  'openai_compatible',
] as const satisfies readonly AiProvider[]

/** In the sequence in which the API runs them. */
export const REDACTION_RULES = [
  'company_meta',
  'email',
  'iban',
  'card',
  'uk_ni',
  'phone',
] as const satisfies readonly RedactionRule[]

/** The rule that the API runs also when the list does not have it. */
export const LOCKED_RULE: RedactionRule = 'company_meta'

/** The drivers that send ticket text to another organisation (MRS 5.1). */
const THIRD_PARTY: ReadonlySet<string> = new Set<AiProvider>(['anthropic', 'openai', 'gemini'])

/**
 * True when a company refuses third parties and its provider override names
 * one. The refusal is the stronger rule (MRS 5.1): the override has no effect.
 */
export function thirdPartyConflict(flags: {
  ai_third_party_allowed: boolean
  ai_provider_override: string | null
}): boolean {
  if (flags.ai_third_party_allowed || flags.ai_provider_override === null) return false
  return THIRD_PARTY.has(flags.ai_provider_override)
}

/** The groups of the AI tab, in the sequence of their tabs. Each is an address: `/settings/ai/cost`. */
export const AI_GROUPS = ['provider', 'triage', 'cost', 'privacy', 'advanced'] as const

export type AiGroup = (typeof AI_GROUPS)[number]

/**
 * The group of each field, so a refused field can name its group. The switch
 * of the whole helpdesk is above the groups and has none. The type refuses a
 * field of the contract that is not here.
 */
const GROUP_OF: Record<keyof AiSettingsUpdate, AiGroup | null> = {
  global_enabled: null,
  provider: 'provider',
  model: 'provider',
  temperature: 'provider',
  max_tokens: 'provider',
  confidence_threshold: 'triage',
  window_days: 'triage',
  hourly_run_cap: 'triage',
  default_monthly_cost_cap_micros: 'cost',
  redaction_rules: 'privacy',
  graduation_threshold: 'advanced',
  graduation_min_runs: 'advanced',
  sandbox_cpu: 'advanced',
  sandbox_mem_mb: 'advanced',
  sandbox_wall_seconds: 'advanced',
  sandbox_repo_mb: 'advanced',
  sandbox_files: 'advanced',
  sandbox_chars: 'advanced',
}

/** The groups that hold a field with a problem, in the sequence of their tabs. */
export function groupsWithProblems(errors: Record<string, string | undefined>): AiGroup[] {
  const groups = Object.entries(GROUP_OF)
    .filter(([field]) => errors[field] !== undefined)
    .map(([, group]) => group)
  return AI_GROUPS.filter((group) => groups.includes(group))
}

// The API counts money in micros: one micro is one millionth of a US dollar.
// The screens show and take US dollars.
const MICROS_PER_DOLLAR = 1_000_000

export function dollarsOf(micros: number): number {
  return micros / MICROS_PER_DOLLAR
}

export function microsOf(dollars: number): number {
  return Math.round(dollars * MICROS_PER_DOLLAR)
}
